import React from 'react';
import { AlertTriangle, Clock, FolderOpen, User } from 'lucide-react';
import type { ProblemExecutiveSummary, ProblemQuickFilter } from '../../services/problemDashboardData';

type DecisionFilter = Extract<ProblemQuickFilter, 'open' | 'critical' | 'sla_overdue' | 'unassigned'>;

interface DecisionItem {
  id: DecisionFilter;
  label: string;
  value: number;
  icon: React.ReactNode;
  hot: boolean;
}

interface ProblemExecutiveStripProps {
  summary: ProblemExecutiveSummary;
  active: ProblemQuickFilter | null;
  onChange: (filter: ProblemQuickFilter | null) => void;
}

export const ProblemExecutiveStrip: React.FC<ProblemExecutiveStripProps> = ({
  summary,
  active,
  onChange,
}) => {
  const items: DecisionItem[] = [
    {
      id: 'open',
      label: 'Abertos',
      value: summary.open,
      icon: <FolderOpen size={14} />,
      hot: summary.open > 0,
    },
    {
      id: 'critical',
      label: 'Críticos',
      value: summary.critical,
      icon: <AlertTriangle size={14} />,
      hot: summary.critical > 0,
    },
    {
      id: 'sla_overdue',
      label: 'Prazo vencido',
      value: summary.slaOverdue,
      icon: <Clock size={14} />,
      hot: summary.slaOverdue > 0,
    },
    {
      id: 'unassigned',
      label: 'Sem responsável',
      value: summary.unassigned,
      icon: <User size={14} />,
      hot: summary.unassigned > 0,
    },
  ];

  return (
    <div className="grid grid-cols-4 border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900 mobile-grid-keep">
      {items.map(item => {
        const selected = active === item.id;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onChange(selected ? null : item.id)}
            className={`flex min-w-0 items-center gap-3 border-r border-slate-200 px-4 py-2 text-left transition-colors last:border-r-0 dark:border-slate-700 ${
              selected
                ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'
            }`}
          >
            <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded ${
              selected
                ? 'bg-white/15 text-white dark:bg-slate-900/10 dark:text-slate-900'
                : item.hot
                  ? 'bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400'
                  : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
            }`}
            >
              {item.icon}
            </span>
            <span className="min-w-0">
              <span className={`block text-[22px] font-bold leading-none tabular-nums ${
                selected
                  ? ''
                  : item.hot
                    ? 'text-slate-900 dark:text-slate-100'
                    : 'text-slate-500'
              }`}
              >
                {item.value}
              </span>
              <span className={`mt-0.5 block truncate text-[11px] font-medium ${
                selected ? 'text-white/80 dark:text-slate-600' : 'text-slate-500'
              }`}
              >
                {item.label}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default ProblemExecutiveStrip;
